<div class="">
  <div class="max-content-width spaced-sections">
    <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <h2 class="section-title">Data Extraction - Conflict Resolution</h2>
      @if (showBackToDataExtraction()) {
        <button
          type="button"
          class="btn-secondary self-start sm:self-auto"
          (click)="navigateBackToDataExtraction()"
          data-testid="back-to-data-extraction-btn"
        >
          Back to Data Extraction
        </button>
      }
    </div>

    <!-- PI-only notice -->
    @if (piOnlyNotice(); as notice) {
      <div
        class="flex gap-3 rounded-r-lg border-l-4 border-amber-500 bg-amber-50 p-4 text-sm text-amber-800 shadow-sm"
        data-testid="pi-only-notice"
      >
        <div>
          <p class="font-bold">Access Restricted</p>
          <p class="mt-0.5">{{ notice }}</p>
          <a
            class="mt-2 inline-block text-xs font-semibold text-amber-700 underline hover:text-amber-900"
            href="/projects"
            >Back to Projects</a
          >
        </div>
      </div>
    } @else {
      <!-- Load error -->
      @if (loadError(); as err) {
        <div
          class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-center gap-2"
          data-testid="load-error"
        >
          <span class="flex-1">{{ err }}</span>
          <button
            class="btn-small-secondary border-red-200 text-red-600 hover:bg-red-50"
            (click)="loadError.set(null)"
          >
            Dismiss
          </button>
        </div>
      }

      <!-- 409 (or other) completion error banner: server is the source of truth -->
      @if (completeError(); as err) {
        <div
          class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-center gap-2"
          data-testid="complete-error"
        >
          <span class="flex-1">{{ err }}</span>
          <button
            class="btn-small-secondary border-red-200 text-red-600 hover:bg-red-50"
            (click)="completeError.set(null)"
          >
            Dismiss
          </button>
        </div>
      }

      <!-- Count cards -->
      <div class="grid-2-col">
        <div class="card card-padding">
          <h3 class="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            Unresolved Conflicts
          </h3>
          <span class="text-2xl font-bold text-red-600" data-testid="unresolved-count">{{
            unresolvedCount()
          }}</span>
          <p class="text-xs text-slate-400 mt-1">Papers awaiting PI decision</p>
        </div>
        <div class="card card-padding">
          <h3 class="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            Resolved Conflicts
          </h3>
          <span class="text-2xl font-bold text-green-600" data-testid="resolved-count">{{
            resolvedCount()
          }}</span>
          <p class="text-xs text-slate-400 mt-1">Conflicts resolved so far</p>
        </div>
      </div>

      <div>
        <app-data-extraction-agreement-card
          [visible]="true"
          [projectId]="projectId.toString()"
          [projectState]="projectState()"
          [preferOfficial]="true"
        ></app-data-extraction-agreement-card>
      </div>

      <!-- Loading -->
      @if (loading()) {
        <div class="card card-padding flex flex-col items-center justify-center min-h-[200px]">
          <div
            class="w-10 h-10 border-4 border-secondary border-t-transparent rounded-full animate-spin"
          ></div>
          <p class="text-sm text-slate-500 mt-4">Loading conflicts...</p>
        </div>
      } @else {
        <!-- Awaiting-decision list -->
        <div class="card p-0 overflow-hidden">
          <div class="px-5 py-3 bg-slate-50 border-b border-slate-200/60">
            <h3 class="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Awaiting PI Decision
            </h3>
          </div>
          @if (unresolved().length === 0) {
            <div class="px-5 py-6 text-center text-sm text-slate-400" data-testid="no-awaiting">
              No conflicts are awaiting a decision.
            </div>
          } @else {
            @for (c of unresolved(); track c.paper.paperToProjectId) {
              <div
                class="flex items-center gap-3 px-5 py-4 border-b border-slate-200/60 last:border-b-0"
                data-testid="awaiting-row"
              >
                <div class="min-w-0 flex-1">
                  <p class="font-semibold text-sm text-slate-900 truncate">{{ c.paper.title }}</p>
                  <div class="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                    @if (c.paper.publicationDate) {
                      <span class="text-xs text-slate-400">{{
                        c.paper.publicationDate | date: 'yyyy'
                      }}</span>
                    }
                    @if (c.paper.authors) {
                      <span class="text-xs text-slate-400 truncate">{{ c.paper.authors }}</span>
                    }
                  </div>
                </div>
                <span
                  class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 shrink-0"
                >
                  {{ c.conflictCount }} conflict{{ c.conflictCount === 1 ? '' : 's' }}
                </span>
                <a
                  class="btn-primary text-sm shrink-0"
                  [routerLink]="['./', c.paper.paperToProjectId]"
                  data-testid="review-link"
                >
                  Review
                </a>
              </div>
            }
          }
        </div>

        <!-- Resolved list behind a toggle -->
        <div class="card p-0 overflow-hidden">
          <button
            class="w-full flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-slate-200/60 text-left hover:bg-slate-100 transition-colors"
            (click)="toggleResolved()"
            data-testid="toggle-resolved"
          >
            <span class="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {{ showResolved() ? 'Hide' : 'Show' }} resolved ({{ resolvedCount() }})
            </span>
            <i
              class="bi text-slate-400 text-xs"
              [class.bi-chevron-down]="!showResolved()"
              [class.bi-chevron-up]="showResolved()"
            ></i>
          </button>
          @if (showResolved()) {
            @if (resolved().length === 0) {
              <div class="px-5 py-6 text-center text-sm text-slate-400">
                No conflicts resolved yet.
              </div>
            } @else {
              <div class="px-5 py-3 border-b border-slate-200/60">
                <input
                  type="text"
                  class="input-field"
                  placeholder="Search title, author, year"
                  [value]="resolvedQuery()"
                  (input)="resolvedQuery.set($any($event.target).value)"
                  data-testid="resolved-search"
                />
              </div>
              <div class="max-h-96 overflow-y-auto">
                @if (filteredResolved().length === 0) {
                  <div
                    class="px-5 py-6 text-center text-sm text-slate-400"
                    data-testid="resolved-empty"
                  >
                    No papers match your search.
                  </div>
                } @else {
                  @for (c of filteredResolved(); track c.paper.paperToProjectId) {
                    <div
                      class="flex items-center gap-3 px-5 py-4 border-b border-slate-200/60 last:border-b-0 bg-green-50/40"
                      data-testid="resolved-row"
                    >
                      <div class="min-w-0 flex-1">
                        <p
                          class="font-semibold text-sm text-slate-900 break-words"
                          data-testid="resolved-title"
                        >
                          {{ c.paper.title }}
                        </p>
                        <div class="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                          @if (c.paper.publicationDate) {
                            <span class="text-xs text-slate-400">{{
                              c.paper.publicationDate | date: 'yyyy'
                            }}</span>
                          }
                          @if (c.paper.authors) {
                            <span
                              class="text-xs text-slate-400 break-words"
                              data-testid="resolved-authors"
                              >{{ c.paper.authors }}</span
                            >
                          }
                        </div>
                      </div>
                      <span
                        class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200 shrink-0"
                      >
                        <i class="bi bi-check2-circle"></i> Resolved
                      </span>
                      <a
                        class="btn-secondary text-sm shrink-0"
                        [routerLink]="['./', c.paper.paperToProjectId]"
                        data-testid="resolved-view-link"
                      >
                        View
                      </a>
                    </div>
                  }
                }
              </div>
            }
          }
        </div>

        <!-- Complete Data Extraction -->
        @if (!isCompleted()) {
          <div>
            <button
              class="w-full"
              [class.btn-primary]="canComplete() && !completing()"
              [class.btn-disabled-primary]="!canComplete() || completing()"
              [disabled]="!canComplete() || completing()"
              (click)="onCompleteClick()"
              data-testid="complete-btn"
            >
              {{ completing() ? 'Completing...' : 'Complete Data Extraction' }}
            </button>
            @if (completeBlockedReason(); as reason) {
              <p
                class="text-center text-xs text-slate-500 mt-1"
                data-testid="complete-blocked-reason"
              >
                {{ reason }}
              </p>
            }
          </div>
        } @else {
          <div class="card card-padding" data-testid="de-completed-banner">
            <div class="empty-state">
              <div class="empty-state-icon">&#10003;</div>
              <h3 class="empty-state-title">Data extraction completed</h3>
              <p class="empty-state-message">
                Completed on {{ completedAt() | date }}. {{ resolvedCount() }} conflicts resolved.
              </p>
            </div>
          </div>
        }
      }
    }
  </div>
</div>

<app-confirm-modal
  [open]="confirmOpen()"
  title="Complete Data Extraction?"
  message="This makes the project read-only and cannot be undone."
  confirmText="Complete"
  cancelText="Cancel"
  confirmVariant="danger"
  [loading]="completing()"
  (openChange)="onConfirmOpenChange($event)"
  (confirmed)="onConfirmComplete()"
></app-confirm-modal>
